import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getClip, reviseSegments, type SegmentRevision } from '../db';
import { extractMono, sliceToWav } from '../services/audioSlicer';
import type { Segment, SegmentKind } from '../types';
import { computePeaks, peakAmplitude, type Peaks } from './wavePeaks';
import {
  applyPlan,
  clampDelta,
  describeRipple,
  isAnchor,
  MIN_MOVE_SEC,
  planMove,
  snapDelta,
  validateMoved,
  type MovedRow,
  type MovePlan,
} from './laneMove';
import { snapTargetsFor, useTaskAudio, useTaskSegments } from './taskAudio';
import { clamp } from '../lib/num';

/**
 * The always-on waveform lane — "I want to see the audio, not only when I'm
 * fixing something".
 *
 * A window of the ORIGINAL task audio around the current clip, with every
 * segment inside that window drawn as a translucent coloured tile over the
 * waveform. It answers, at a glance and without opening anything, the two
 * questions the queue cannot: is this cut sitting on the sound it claims to be
 * sitting on, and what is around it.
 *
 * WHAT IT IS NOT
 * --------------
 * It is not a second trimmer. Edges are not draggable here: the tile edges hand
 * off to {@link WaveTrimmer}, which already owns resizing and does it on a
 * zoomed window where 10 ms is a visible distance. What the lane owns is the
 * other half of the problem — a cut that is the RIGHT LENGTH in the WRONG PLACE
 * — which the trimmer can only express as two co-ordinated edge drags.
 *
 * DRAG-TO-ALIGN
 * -------------
 * Dragging a tile's body slides the whole segment, duration preserved, inside a
 * ±1 s corridor, magnetically snapping its start onto detected onsets and speech
 * rises. On drop, the segments that follow it shift by the same amount — but
 * only while they were near-contiguous with it and only until the first row a
 * human has already touched. See `laneMove.ts` for those rules; the wall they
 * compute is applied to the drag itself, so the user feels it rather than being
 * told about it afterwards.
 *
 * Nothing is written until Apply. Apply is ONE `reviseSegments` transaction —
 * every moved row and its freshly cut clip together — and it keeps the clips it
 * replaced so Undo can put the exact original bytes back.
 */

/* ------------------------------------------------------------------ */
/* Props                                                               */
/* ------------------------------------------------------------------ */

/**
 * A media element whose `currentTime` should drive the playhead.
 *
 * `kind` exists because the two players measure time differently: the context
 * and karaoke players play the ORIGINAL file, so their clock IS file time, while
 * the clip player plays a pre-cut WAV that starts somewhere inside the file. For
 * a clip we recover that origin from its DURATION rather than from the slicer's
 * padding constants — the padding is symmetric, so `(clipDur - span) / 2` is the
 * lead-in whatever options the clip was cut with, and the lane stays correct if
 * those options ever change.
 */
export type PlayheadSource =
  | { kind: 'file'; ref: React.RefObject<HTMLAudioElement | null>; active: boolean }
  | {
      kind: 'clip';
      ref: React.RefObject<HTMLAudioElement | null>;
      active: boolean;
      /** Which segment the clip belongs to. */
      segmentId: string;
      /** The clip's own length in seconds (0 before metadata loads). */
      clipDurSec: number;
    };

export interface WaveLaneProps {
  taskId: string;
  /** The clip the page is showing. The window centres on it and it is drawn brighter. */
  currentSegmentId: string | null;
  /** Seconds of audio shown either side of the current clip. */
  padSec?: number;
  /**
   * `full` draws the waveform and the legend (Verify, Done).
   * `strip` starts as a slim tile-only bar that expands to `full` on tap
   * (Correct, where a card list would otherwise pay for thirty waveforms).
   */
  variant?: 'full' | 'strip';
  /** Allow drag-to-align. */
  editable?: boolean;
  /** Task length, used until the decode lands. */
  durationSec?: number;
  playheads?: PlayheadSource[];
  /** A tap on a tile's edge asks the page to open the real trimmer. */
  onRequestTrim?: (segmentId: string) => void;
  /** Boundaries were persisted (Apply or Undo) — pages refresh their own rows. */
  onMoved?: (moves: Array<{ id: string; startSec: number; endSec: number }>) => void;
  className?: string;
}

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

/** Lane height in CSS pixels (the waveform canvas). */
const LANE_H = 76;
/** Pixels of horizontal travel before a touch counts as a drag and not a tap. */
const ARM_PX = 4;
/** Tile edges narrower than this get no hand-off zone — the body wins. */
const EDGE_MIN_TILE_PX = 34;

const KINDS: SegmentKind[] = ['word', 'filler', 'noise', 'unclear', 'silence'];
const KIND_LABEL: Record<SegmentKind, string> = {
  word: 'word',
  filler: 'filler',
  noise: 'noise',
  unclear: 'unclear',
  silence: 'silence',
};

function cssVar(el: Element | null, name: string, fallback: string): string {
  if (!el) return fallback;
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return v || fallback;
}

/**
 * Where a pre-cut clip starts inside the original file.
 * See {@link PlayheadSource} for why this is derived from the clip's duration.
 */
export function clipOriginSec(
  startSec: number,
  endSec: number,
  clipDurSec: number,
  fileDurationSec: number,
): number {
  const span = Math.max(0, endSec - startSec);
  const lead = Math.max(0, clipDurSec - span) / 2;
  return clamp(startSec - lead, 0, Math.max(0, fileDurationSec - clipDurSec));
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

interface DragState {
  plan: MovePlan;
  pointerId: number;
  startX: number;
  /** Horizontal intent confirmed — we now own the gesture. */
  armed: boolean;
  delta: number;
  snappedTo: number | null;
}

interface PendingState {
  plan: MovePlan;
  delta: number;
  snappedTo: number | null;
}

interface UndoState {
  /** The rows exactly as they were, with the clip bytes they had. */
  rows: Array<{ segment: Segment; clip: Blob | undefined }>;
  delta: number;
}

export function WaveLane({
  taskId,
  currentSegmentId,
  padSec = 2.5,
  variant = 'full',
  editable = false,
  durationSec,
  playheads,
  onRequestTrim,
  onMoved,
  className = '',
}: WaveLaneProps) {
  const [expanded, setExpanded] = useState(variant === 'full');
  const open = variant === 'full' || expanded;

  const segments = useTaskSegments(taskId);
  const { audio, loading, error: audioError } = useTaskAudio(taskId, open);

  const stageRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const headRef = useRef<HTMLDivElement | null>(null);

  const [cssWidth, setCssWidth] = useState(0);
  const [peaks, setPeaks] = useState<Peaks | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [pendingMove, setPendingMove] = useState<PendingState | null>(null);
  const [applying, setApplying] = useState(false);
  const [undoState, setUndoState] = useState<UndoState | null>(null);
  const [error, setError] = useState<string | null>(null);

  const dragRef = useRef<DragState | null>(null);
  dragRef.current = drag;

  /* ---------------- window ---------------- */

  const current = useMemo(
    () => segments.find((s) => s.id === currentSegmentId) ?? null,
    [segments, currentSegmentId],
  );

  const fileDur =
    audio?.durationSec ??
    durationSec ??
    (segments.length ? segments[segments.length - 1].endSec : 0);

  const win = useMemo(() => {
    if (!current) {
      return { start: 0, end: Math.max(0.5, Math.min(fileDur || 2 * padSec, 2 * padSec)) };
    }
    const start = Math.max(0, current.startSec - padSec);
    const end = Math.min(fileDur > 0 ? fileDur : current.endSec + padSec, current.endSec + padSec);
    return { start, end: Math.max(start + 0.2, end) };
  }, [current, padSec, fileDur]);

  const span = Math.max(1e-6, win.end - win.start);
  const pctOf = useCallback((t: number) => clamp(((t - win.start) / span) * 100, -20, 120), [win.start, span]);

  /* ---------------- measure ---------------- */

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => setCssWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open]);

  /* ---------------- peaks ---------------- */

  useEffect(() => {
    if (!open || !audio || cssWidth <= 0) {
      setPeaks(null);
      return;
    }
    let cancelled = false;
    // One column per device pixel, capped: past 2x the extra columns are
    // invisible on a phone and the reduction is the only real cost here.
    const dpr = Math.min(2, Math.max(1, window.devicePixelRatio || 1));
    const columns = Math.max(1, Math.round(cssWidth * dpr));
    // Defer so a card that just mounted paints before we chew through PCM.
    const id = window.setTimeout(() => {
      if (cancelled) return;
      const mono = extractMono(audio.buffer, win.start, win.end);
      setPeaks(computePeaks(mono, columns));
    }, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [open, audio, cssWidth, win.start, win.end]);

  /* ---------------- draw ---------------- */

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || cssWidth <= 0) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(2, Math.max(1, window.devicePixelRatio || 1));
    const w = Math.max(1, Math.round(cssWidth * dpr));
    const h = Math.round(LANE_H * dpr);
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, w, h);
    if (!peaks) return;

    const wave = cssVar(canvas, '--wl-wave', '#8ea0bd');
    const line = cssVar(canvas, '--border', '#2b3342');

    const mid = h / 2;
    const amp = peakAmplitude(peaks);
    const gain = amp > 0 ? Math.min(6, 0.9 / amp) : 1;
    const half = mid - 3 * dpr;
    const cols = Math.min(peaks.columns, w);

    ctx.fillStyle = wave;
    ctx.globalAlpha = 0.62;
    for (let c = 0; c < cols; c++) {
      const lo = clamp(peaks.min[c] * gain, -1, 1);
      const hi = clamp(peaks.max[c] * gain, -1, 1);
      const y0 = mid - hi * half;
      const y1 = mid - lo * half;
      ctx.fillRect(c, y0, 1, Math.max(dpr, y1 - y0));
    }
    ctx.globalAlpha = 1;

    ctx.fillStyle = line;
    ctx.fillRect(0, mid - dpr / 2, w, Math.max(1, dpr / 2));
  }, [peaks, cssWidth]);

  useEffect(() => {
    draw();
  }, [draw]);

  /* ---------------- playhead ---------------- */

  const sourcesRef = useRef<PlayheadSource[]>([]);
  sourcesRef.current = playheads ?? [];
  const anyActive = (playheads ?? []).some((p) => p.active);

  const segmentsRef = useRef<Segment[]>(segments);
  segmentsRef.current = segments;
  const winRef = useRef(win);
  winRef.current = win;
  const fileDurRef = useRef(fileDur);
  fileDurRef.current = fileDur;

  useEffect(() => {
    const node = headRef.current;
    if (!node) return;
    if (!anyActive || !open) {
      node.style.opacity = '0';
      return;
    }

    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const el = headRef.current;
      if (!el) return;
      let t: number | null = null;
      for (const src of sourcesRef.current) {
        if (!src.active) continue;
        const media = src.ref.current;
        if (!media || media.paused || !Number.isFinite(media.currentTime)) continue;
        if (src.kind === 'file') {
          t = media.currentTime;
        } else {
          const seg = segmentsRef.current.find((s) => s.id === src.segmentId);
          if (!seg) continue;
          const dur = src.clipDurSec > 0 ? src.clipDurSec : media.duration;
          if (!Number.isFinite(dur) || dur <= 0) continue;
          t = clipOriginSec(seg.startSec, seg.endSec, dur, fileDurRef.current) + media.currentTime;
        }
        break;
      }
      const w = winRef.current;
      if (t === null || t < w.start || t > w.end) {
        el.style.opacity = '0';
        return;
      }
      const pct = ((t - w.start) / Math.max(1e-6, w.end - w.start)) * 100;
      el.style.opacity = '1';
      el.style.left = `${pct}%`;
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      const el = headRef.current;
      if (el) el.style.opacity = '0';
    };
  }, [anyActive, open]);

  /* ---------------- snap targets, warmed off the critical path ---------------- */

  const snapRef = useRef<Float64Array>(new Float64Array(0));
  useEffect(() => {
    if (!open || !editable || !audio) {
      snapRef.current = new Float64Array(0);
      return;
    }
    let cancelled = false;
    // The corridor reaches a second past either edge of the window, so the
    // analysis has to as well or the magnet dies at the rim.
    const id = window.setTimeout(() => {
      if (cancelled) return;
      snapRef.current = snapTargetsFor(audio, win.start - 1.2, win.end + 1.2);
    }, 220);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [open, editable, audio, win.start, win.end]);

  /* ---------------- reset transient state when the card changes ---------------- */

  useEffect(() => {
    setDrag(null);
    setPendingMove(null);
    setUndoState(null);
    setError(null);
  }, [currentSegmentId, taskId]);

  /* ---------------- dragging ---------------- */

  const canDrag = editable && open && Boolean(audio) && !applying && !pendingMove;

  const beginDrag = useCallback(
    (segmentId: string) => (e: React.PointerEvent<HTMLDivElement>) => {
      if (!canDrag) return;
      const plan = planMove(segmentsRef.current, segmentId, fileDurRef.current);
      if (!plan) return;
      if (plan.maxDelta <= 0 && plan.minDelta >= 0) return; // pinned on both sides
      setError(null);
      setDrag({ plan, pointerId: e.pointerId, startX: e.clientX, armed: false, delta: 0, snappedTo: null });
    },
    [canDrag],
  );

  const moveDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const stage = stageRef.current;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    if (rect.width <= 0) return;

    const dx = e.clientX - d.startX;
    if (!d.armed) {
      // Until horizontal intent is clear the browser keeps the gesture, so a
      // finger that meant to scroll the Correct list still scrolls it.
      if (Math.abs(dx) < ARM_PX) return;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* synthetic pointer — handlers stay on this element anyway */
      }
    }
    e.preventDefault();
    const w = winRef.current;
    const raw = (dx / rect.width) * (w.end - w.start);
    const snapped = snapDelta(d.plan, raw, snapRef.current);
    setDrag({ ...d, armed: true, delta: snapped.delta, snappedTo: snapped.snappedTo });
  }, []);

  const endDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* pointer already gone */
    }
    setDrag(null);
    if (d.armed && Math.abs(d.delta) >= MIN_MOVE_SEC) {
      setPendingMove({ plan: d.plan, delta: d.delta, snappedTo: d.snappedTo });
    }
  }, []);

  const cancelDrag = useCallback(() => setDrag(null), []);

  /** Arrow keys nudge the current tile: 10 ms, or 100 ms with Shift. */
  const onTileKey = useCallback(
    (segmentId: string) => (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!canDrag && !pendingMove) return;
      const dir = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
      if (dir === 0) return;
      e.preventDefault();
      const step = (e.shiftKey ? 0.1 : 0.01) * dir;
      const plan =
        pendingMove?.plan.segmentId === segmentId
          ? pendingMove.plan
          : planMove(segmentsRef.current, segmentId, fileDurRef.current);
      if (!plan) return;
      const base = pendingMove?.plan.segmentId === segmentId ? pendingMove.delta : 0;
      const next = clampDelta(plan, base + step);
      if (Math.abs(next) < MIN_MOVE_SEC) {
        setPendingMove(null);
        return;
      }
      setPendingMove({ plan, delta: next, snappedTo: null });
    },
    [canDrag, pendingMove],
  );

  /* ---------------- apply / cancel / undo ---------------- */

  const live = drag?.armed ? { plan: drag.plan, delta: drag.delta } : pendingMove;

  const movedRows: MovedRow[] = useMemo(() => {
    if (!live) return [];
    return applyPlan(segments, live.plan, live.delta);
  }, [live, segments]);

  const apply = useCallback(async () => {
    if (!pendingMove || !audio || applying) return;
    const rows = applyPlan(segments, pendingMove.plan, pendingMove.delta);
    if (rows.length === 0) return;
    const bad = validateMoved(segments, rows);
    if (bad) {
      setError(bad);
      return;
    }

    setApplying(true);
    setError(null);
    try {
      // Read the clips BEFORE they are replaced: Undo restores these exact
      // bytes rather than re-cutting, so an undone move is byte-identical.
      const prevClips = await Promise.all(rows.map((r) => getClip(r.segment.id)));
      const revisions: SegmentRevision[] = rows.map((r, i) => ({
        segment:
          i === 0
            ? // A human put THIS cut here — same ground-truth promotion the
              // trimmer makes. The rows swept along are still machine guesses
              // that merely kept their spacing, so promoting them would forge
              // human placements and anchor them against future ripples.
              { ...r.segment, startSec: r.startSec, endSec: r.endSec, confidence: 1, placedBy: 'human' as const }
            : { ...r.segment, startSec: r.startSec, endSec: r.endSec },
        clip: sliceToWav(audio.buffer, r.startSec, r.endSec),
      }));
      await reviseSegments(revisions);
      setUndoState({
        rows: rows.map((r, i) => ({ segment: r.segment, clip: prevClips[i] })),
        delta: pendingMove.delta,
      });
      setPendingMove(null);
      onMoved?.(
        revisions.map((r) => ({
          id: r.segment.id,
          startSec: r.segment.startSec,
          endSec: r.segment.endSec,
        })),
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not move those clips.');
    } finally {
      setApplying(false);
    }
  }, [pendingMove, audio, applying, segments, onMoved]);

  const undoMove = useCallback(async () => {
    if (!undoState || applying) return;
    setApplying(true);
    setError(null);
    try {
      await reviseSegments(
        // A row whose clip could not be read is restored without one: its
        // boundaries go back, and the (wrongly cut) blob is left rather than
        // deleted, because a segment with no clip at all is unplayable.
        undoState.rows.map((r) => ({ segment: r.segment, clip: r.clip })),
      );
      onMoved?.(
        undoState.rows.map((r) => ({
          id: r.segment.id,
          startSec: r.segment.startSec,
          endSec: r.segment.endSec,
        })),
      );
      setUndoState(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not undo that move.');
    } finally {
      setApplying(false);
    }
  }, [undoState, applying, onMoved]);

  /* ---------------- tiles ---------------- */

  const visible = useMemo(
    () => segments.filter((s) => s.endSec > win.start - 0.05 && s.startSec < win.end + 0.05),
    [segments, win.start, win.end],
  );

  const movedById = useMemo(() => {
    const m = new Map<string, MovedRow>();
    for (const r of movedRows) m.set(r.segment.id, r);
    return m;
  }, [movedRows]);

  const draggedId = live?.plan.segmentId ?? null;

  const followerCount = live ? Math.max(0, movedRows.length - 1) : 0;
  const liveDelta = live?.delta ?? 0;

  /* ---------------- render ---------------- */

  if (variant === 'strip' && !expanded) {
    return (
      <button
        type="button"
        className={`wl-strip ${className}`.trim()}
        onClick={() => setExpanded(true)}
        aria-label="Show the waveform for this clip"
        aria-expanded={false}
        data-lane-strip={taskId}
      >
        <span className="wl-strip__rail" aria-hidden="true">
          {visible.map((s) => (
            <i
              key={s.id}
              className={`wl-strip__tile wl-tile--${s.kind}${s.id === currentSegmentId ? ' is-current' : ''}`}
              style={{ left: `${pctOf(s.startSec)}%`, width: `${Math.max(0.8, pctOf(s.endSec) - pctOf(s.startSec))}%` }}
            />
          ))}
        </span>
        <span className="wl-strip__hint">waveform</span>
      </button>
    );
  }

  return (
    <section
      className={`wl ${className}`.trim()}
      aria-label="Waveform around this clip"
      data-lane={taskId}
      data-lane-current={currentSegmentId ?? ''}
    >
      <div
        className={`wl__stage${drag?.armed ? ' is-dragging' : ''}`}
        ref={stageRef}
        style={{ height: `${LANE_H}px` }}
      >
        <canvas className="wl__canvas" ref={canvasRef} style={{ height: `${LANE_H}px` }} aria-hidden="true" />

        {/* origin outlines for everything the live move is displacing */}
        {live &&
          movedRows.map((r) => (
            <div
              key={`origin-${r.segment.id}`}
              className="wl-origin"
              aria-hidden="true"
              style={{
                left: `${pctOf(r.segment.startSec)}%`,
                width: `${Math.max(0.6, pctOf(r.segment.endSec) - pctOf(r.segment.startSec))}%`,
              }}
            />
          ))}

        {visible.map((s) => {
          const m = movedById.get(s.id);
          const start = m ? m.startSec : s.startSec;
          const end = m ? m.endSec : s.endSec;
          const left = pctOf(start);
          const width = Math.max(0.6, pctOf(end) - left);
          const isCurrent = s.id === currentSegmentId;
          const isDragged = s.id === draggedId;
          const isGhost = Boolean(m) && !isDragged;
          const widthPx = (width / 100) * Math.max(1, cssWidth);
          const showEdges = editable && isCurrent && Boolean(onRequestTrim) && widthPx >= EDGE_MIN_TILE_PX;
          const classes = [
            'wl-tile',
            `wl-tile--${s.kind}`,
            isCurrent ? 'is-current' : '',
            isDragged ? 'is-dragged' : '',
            isGhost ? 'is-ghost' : '',
            drag?.armed && drag.snappedTo !== null && isDragged ? 'is-snapped' : '',
            isAnchor(s) ? 'is-anchor' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <div
              key={s.id}
              className={classes}
              style={{ left: `${left}%`, width: `${width}%` }}
              data-seg={s.id}
              data-kind={s.kind}
              data-start={start.toFixed(3)}
              data-end={end.toFixed(3)}
              data-anchor={isAnchor(s) ? '1' : '0'}
              role={editable ? 'button' : undefined}
              tabIndex={editable && isCurrent ? 0 : undefined}
              aria-label={
                editable
                  ? `${s.heard || s.kind} at ${s.startSec.toFixed(2)} seconds — drag to move`
                  : undefined
              }
              title={`${s.heard || KIND_LABEL[s.kind]} · ${s.startSec.toFixed(2)}–${s.endSec.toFixed(2)}s`}
              onPointerDown={editable ? beginDrag(s.id) : undefined}
              onPointerMove={editable ? moveDrag : undefined}
              onPointerUp={editable ? endDrag : undefined}
              onPointerCancel={editable ? cancelDrag : undefined}
              onKeyDown={editable && isCurrent ? onTileKey(s.id) : undefined}
            >
              <span className="wl-tile__label">{s.heard || KIND_LABEL[s.kind]}</span>
              {showEdges && (
                <>
                  <span
                    className="wl-tile__edge wl-tile__edge--start"
                    aria-hidden="true"
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onRequestTrim?.(s.id);
                    }}
                  />
                  <span
                    className="wl-tile__edge wl-tile__edge--end"
                    aria-hidden="true"
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onRequestTrim?.(s.id);
                    }}
                  />
                </>
              )}
            </div>
          );
        })}

        {drag?.armed && drag.snappedTo !== null && (
          <div className="wl__snap" aria-hidden="true" style={{ left: `${pctOf(drag.snappedTo)}%` }} />
        )}

        <div className="wl__head" ref={headRef} aria-hidden="true" style={{ opacity: 0 }} />

        {loading && !peaks && <div className="wl__veil">decoding…</div>}
        {audioError && <div className="wl__veil wl__veil--err">{audioError}</div>}
      </div>

      <div className="wl__foot">
        <div className="wl__legend" aria-label="Colour key">
          {KINDS.map((k) => (
            <span key={k} className="wl__key">
              <i className={`wl__swatch wl-tile--${k}`} aria-hidden="true" />
              {KIND_LABEL[k]}
            </span>
          ))}
        </div>
        {variant === 'strip' && (
          <button type="button" className="wl__collapse" onClick={() => setExpanded(false)}>
            hide
          </button>
        )}
      </div>

      {editable && !live && !undoState && (
        <p className="wl__hint">
          {audio ? 'Drag a clip sideways to re-align it. Its edges open the trimmer.' : 'Loading audio…'}
        </p>
      )}

      {live && (
        <div className="wl__preview" role="status">
          <span className="wl__preview__text">
            {describeRipple(followerCount, liveDelta)}
            {live.plan.stop === 'anchor' ? (
              <em className="wl__preview__stop">
                {' '}
                stops at “{live.plan.stopLabel || '—'}” (already judged)
              </em>
            ) : null}
            {live.plan.stop === 'gap' ? <em className="wl__preview__stop"> stops at the next pause</em> : null}
          </span>
          {pendingMove && (
            <span className="wl__preview__btns">
              <button type="button" className="wl__btn" onClick={() => setPendingMove(null)} disabled={applying}>
                Cancel
              </button>
              <button
                type="button"
                className="wl__btn wl__btn--primary"
                onClick={() => void apply()}
                disabled={applying}
              >
                {applying ? 'Applying…' : 'Apply'}
              </button>
            </span>
          )}
        </div>
      )}

      {undoState && (
        <div className="wl__undo" role="status">
          <span>
            Moved {undoState.rows.length} clip{undoState.rows.length === 1 ? '' : 's'} by{' '}
            {undoState.delta >= 0 ? '+' : '−'}
            {Math.abs(undoState.delta).toFixed(2)}s
          </span>
          <span className="spacer" />
          <button type="button" className="wl__btn" onClick={() => void undoMove()} disabled={applying}>
            ↩ Undo
          </button>
          <button
            type="button"
            className="wl__btn wl__btn--quiet"
            onClick={() => setUndoState(null)}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {error && (
        <p className="wl__err" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
